/**
 * Unified "New item" wizard.
 *
 * One entry point for creating any operational record. The user picks the
 * type up front, then walks through tightly-focused steps with hints. Each
 * step asks for one thing.
 *
 * Why one wizard: officers find six separate "New X" pages confusing. One
 * dropdown, one flow, one mental model.
 *
 * Steps (dynamic per type):
 *   1. Type            — what kind of thing?
 *   2. Where           — constituency → ward → branch (cascading)
 *   3. Subject (some)  — who is this about (perf, pop, poi)?
 *   4. Details         — title/body/reasons (the meat)
 *   5. How sensitive   — classification / severity / priority / threat
 *   6. Review + submit
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ApiError } from "../../api/client";
import { HierarchyApi, type Branch, type Constituency, type Ward } from "../../admin/hierarchy/api";
import { PollingStationsApi, type PollingStation } from "../../admin/pollingStations/api";
import { RbacApi, type AppUser } from "../../admin/rbac/api";
import { CasesApi } from "../cases/api";
import { IntelApi } from "../intel/api";
import { POIApi, POI_TAG_LABELS, POI_TAG_ORDER, type POITag } from "../poi/api";
import { PopularityApi } from "../popularity/api";
import { PerformanceApi } from "../performance/api";
import { TasksApi } from "../tasks/api";
import {
  CheckSquareIcon,
  EyeIcon,
  FileIcon,
  RadioIcon,
  StarIcon,
  TrendingIcon,
} from "../../shell/icons";

type ItemType = "case" | "intel" | "performance" | "popularity" | "poi" | "task";

interface TypeSpec {
  type: ItemType;
  label: string;
  desc: string;
  icon: ReactNode;
  scopeLevel?: "branch" | "ward" | "constituency";  // some types fix this
}

const TYPES: TypeSpec[] = [
  { type: "case", label: "Case", desc: "An investigation or incident you're recording.", icon: <FileIcon />, scopeLevel: "branch" },
  { type: "intel", label: "Intel item", desc: "A quick observation, alert or note from the field.", icon: <RadioIcon />, scopeLevel: "branch" },
  { type: "performance", label: "Performance report", desc: "How a member is doing — ratings and reasons.", icon: <TrendingIcon />, scopeLevel: "branch" },
  { type: "popularity", label: "Popularity report", desc: "How well-known or supported a person is in an area.", icon: <StarIcon /> },
  { type: "poi", label: "Person of interest", desc: "A new person to put on the watchlist.", icon: <EyeIcon /> },
  { type: "task", label: "Task", desc: "Assign work to another officer.", icon: <CheckSquareIcon />, scopeLevel: "branch" },
];

interface FormState {
  // Step 2 — scope
  scopeLevel: "constituency" | "ward" | "branch";
  cid: string;
  wid: string;
  bid: string;
  // Optional polling-station anchor — narrows where the record happened
  // beyond the branch (used for per-station threat reports).
  psid: string;
  // Step 3 — subject (for performance/popularity)
  subjectKind: "internal" | "external";
  subjectUid: string;
  subjectName: string;
  subjectContext: string;
  // Subject for case (PII fields, optional)
  caseSubjectName: string;
  caseSubjectIdNumber: string;
  caseSubjectPhone: string;
  // Step 4 — content
  title: string;
  body: string;
  description: string;
  reasons: string;
  recommendations: string;
  observations: string;
  notes: string;
  aliases: string;
  idNumber: string;
  phone: string;
  email: string;
  address: string;
  dateOfBirth: string;
  // Step 5 — sensitivity / type-specific knobs
  classification: "unclassified" | "restricted" | "secret";
  severity: "low" | "medium" | "high" | "critical";
  // Case kind: picks between formal investigation files and the lighter
  // intel-style records (observation/alert/note). Defaults to "investigation"
  // for type=case; intel captures still send observation/alert/note via the
  // legacy /intel routes until that path is fully retired.
  kind: "investigation" | "observation" | "alert" | "note";
  rating: "excellent" | "good" | "satisfactory" | "poor" | "failing";
  popularityScore: "very_low" | "low" | "medium" | "high" | "very_high";
  threatLevel: "low" | "medium" | "high";
  // POI tags — multi-select. Pulled from POI_TAG_ORDER vocabulary.
  poiTags: string[];
  // Intel metadata — how many sources fed this assessment + numeric (0–100)
  // confidence. Stored as strings so the inputs work cleanly when blank;
  // converted to numbers (or undefined) on submit. Applies to every kind
  // of capture except tasks.
  sourcesCount: string;
  confidenceScore: string;
  // Performance/Task — assignee/subject
  assigneeUid: string;
  priority: "low" | "medium" | "high" | "urgent";
  dueAt: string;
}

const INITIAL: FormState = {
  scopeLevel: "branch",
  cid: "", wid: "", bid: "", psid: "",
  subjectKind: "internal",
  subjectUid: "", subjectName: "", subjectContext: "",
  caseSubjectName: "", caseSubjectIdNumber: "", caseSubjectPhone: "",
  title: "", body: "", description: "",
  reasons: "", recommendations: "", observations: "",
  notes: "", aliases: "",
  idNumber: "", phone: "", email: "", address: "", dateOfBirth: "",
  classification: "unclassified",
  severity: "low", kind: "investigation",
  rating: "satisfactory", popularityScore: "medium",
  threatLevel: "low",
  poiTags: [],
  sourcesCount: "", confidenceScore: "",
  assigneeUid: "", priority: "medium", dueAt: "",
};

export function NewItemWizard() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [type, setType] = useState<ItemType | null>(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [error, setError] = useState<string | null>(null);

  // Cascading hierarchy data
  const constituencies = useQuery({ queryKey: ["constituencies"], queryFn: HierarchyApi.listConstituencies });
  const wards = useQuery({
    queryKey: ["wards", form.cid],
    queryFn: () => HierarchyApi.listWards(form.cid),
    enabled: !!form.cid,
  });
  const branches = useQuery({
    queryKey: ["branches", form.wid],
    queryFn: () => HierarchyApi.listBranches(form.wid),
    enabled: !!form.wid,
  });
  const stations = useQuery({
    queryKey: ["polling-stations", form.bid],
    queryFn: () => PollingStationsApi.listForBranch(form.bid),
    enabled: !!form.bid,
  });
  const users = useQuery({ queryKey: ["users"], queryFn: RbacApi.listUsers });

  const spec = useMemo(() => TYPES.find((t) => t.type === type), [type]);
  const steps = useMemo(() => stepsFor(type), [type]);
  const total = steps.length;

  const update = (patch: Partial<FormState>) => setForm((s) => ({ ...s, ...patch }));

  const submit = useMutation({
    mutationFn: async () => {
      const scopeId =
        form.scopeLevel === "branch" ? form.bid :
        form.scopeLevel === "ward" ? form.wid : form.cid;
      // Polling station only meaningful when the record is anchored at the
      // branch level (or below) — empty string is normalised to undefined so
      // the API doesn't store an empty value.
      const pollingStationId = form.psid || undefined;
      // Intel metadata — convert blank strings to undefined so we don't
      // ship `sourcesCount: 0` when the officer skipped the field.
      const sourcesCount = form.sourcesCount.trim() === "" ? undefined : Number(form.sourcesCount);
      const confidenceScore = form.confidenceScore.trim() === "" ? undefined : Number(form.confidenceScore);
      switch (type) {
        case "case":
          return CasesApi.create({
            kind: form.kind,
            severity: form.severity,
            title: form.title,
            summary: form.body,
            classification: form.classification,
            branchId: form.bid,
            pollingStationId,
            subjectName: form.caseSubjectName || undefined,
            subjectIdNumber: form.caseSubjectIdNumber || undefined,
            subjectPhone: form.caseSubjectPhone || undefined,
            sourcesCount,
            confidenceScore,
          });
        case "intel":
          // Intel is now a thin alias over cases — but until the migration
          // runs in prod we keep using the legacy /intel routes. Coerce
          // "investigation" (case-only) back to "observation" for safety.
          // Intel API doesn't expose sources/confidence yet (legacy);
          // captures via /new still go through cases or wait for migration.
          return IntelApi.publish({
            kind: form.kind === "investigation" ? "observation" : form.kind,
            severity: form.severity,
            classification: form.classification,
            title: form.title,
            body: form.body,
            branchId: form.bid,
            pollingStationId,
          });
        case "performance":
          return PerformanceApi.create({
            subjectUid: form.subjectUid,
            branchId: form.bid,
            pollingStationId,
            rating: form.rating,
            reasons: form.reasons,
            recommendations: form.recommendations || undefined,
            classification: form.classification,
            sourcesCount,
            confidenceScore,
          });
        case "popularity":
          return PopularityApi.create({
            subjectType: form.subjectKind,
            subjectUid: form.subjectKind === "internal" ? form.subjectUid : undefined,
            subjectName: form.subjectKind === "external" ? form.subjectName : undefined,
            subjectContext: form.subjectKind === "external" ? form.subjectContext : undefined,
            scopeLevel: form.scopeLevel,
            scopeId,
            pollingStationId,
            score: form.popularityScore,
            observations: form.observations,
            classification: form.classification,
            sourcesCount,
            confidenceScore,
          });
        case "poi":
          return POIApi.create({
            fullName: form.subjectName,
            aliases: form.aliases.split(",").map((s) => s.trim()).filter(Boolean),
            idNumber: form.idNumber || undefined,
            phone: form.phone || undefined,
            email: form.email || undefined,
            address: form.address || undefined,
            dateOfBirth: form.dateOfBirth || undefined,
            notes: form.notes,
            threatLevel: form.threatLevel,
            classification: form.classification,
            scopeLevel: form.scopeLevel,
            scopeId,
            pollingStationId,
            tags: form.poiTags as POITag[],
            sourcesCount,
            confidenceScore,
          });
        case "task":
          return TasksApi.create({
            title: form.title,
            description: form.description,
            assigneeUid: form.assigneeUid,
            branchId: form.bid,
            pollingStationId,
            priority: form.priority,
            dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : undefined,
          });
      }
      throw new Error("unknown type");
    },
    onSuccess: () => {
      qc.invalidateQueries();
      nav("/app", { replace: true });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });

  function next() { setStep((s) => Math.min(s + 1, total)); }
  function back() { setStep((s) => Math.max(1, s - 1)); }

  // ---- Render --------------------------------------------------------------

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>New item</h1>
        <Link to="/app" className="muted" style={{ fontSize: 13 }}>Cancel</Link>
      </div>
      <p className="muted" style={{ margin: "0 0 18px", fontSize: 14 }}>
        We'll walk you through it step by step. Pick what you want to create, then we'll ask only what's needed.
      </p>

      {type && <Progress current={step} total={total} labels={steps.map((s) => s.title)} />}
      {error && <div className="error">{error}</div>}

      {!type ? (
        <Step1Type
          onPick={(t) => {
            setType(t);
            const next = TYPES.find((x) => x.type === t);
            if (next?.scopeLevel) update({ scopeLevel: next.scopeLevel });
            setStep(2);
          }}
        />
      ) : (
        <div className="card" style={{ padding: 20 }}>
          {step === 1 && <Step1Type onPick={(t) => { setType(t); setStep(2); }} />}

          {step === 2 && (
            <ScopeStep
              spec={spec!}
              form={form}
              update={update}
              constituencies={constituencies.data?.items ?? []}
              wards={wards.data?.items ?? []}
              branches={branches.data?.items ?? []}
              stations={stations.data?.items ?? []}
              ready={isScopeReady(form, spec!)}
              onNext={next}
              onBack={back}
            />
          )}

          {step === 3 && type === "performance" && (
            <PerfSubjectStep
              form={form}
              update={update}
              users={users.data?.items ?? []}
              onNext={next} onBack={back}
            />
          )}
          {step === 3 && type === "popularity" && (
            <PopSubjectStep
              form={form}
              update={update}
              users={users.data?.items ?? []}
              onNext={next} onBack={back}
            />
          )}
          {step === 3 && type === "task" && (
            <TaskAssigneeStep
              form={form}
              update={update}
              users={users.data?.items ?? []}
              onNext={next} onBack={back}
            />
          )}

          {step === stepIndex(steps, "details") && <DetailsStep type={type} form={form} update={update} onNext={next} onBack={back} />}
          {step === stepIndex(steps, "sensitivity") && <SensitivityStep type={type} form={form} update={update} onNext={next} onBack={back} />}
          {step === stepIndex(steps, "review") && (
            <ReviewStep
              type={type}
              spec={spec!}
              form={form}
              busy={submit.isPending}
              onBack={back}
              onSubmit={() => { setError(null); submit.mutate(); }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ---- step plumbing -----------------------------------------------------

interface StepDef { key: string; title: string; }

function stepsFor(type: ItemType | null): StepDef[] {
  if (!type) return [{ key: "type", title: "Type" }];
  const base: StepDef[] = [
    { key: "type", title: "Type" },
    { key: "scope", title: "Where" },
  ];
  if (type === "performance" || type === "popularity") base.push({ key: "subject", title: "Who" });
  if (type === "task") base.push({ key: "subject", title: "Who" });
  base.push({ key: "details", title: "Details" });
  base.push({ key: "sensitivity", title: "How sensitive" });
  base.push({ key: "review", title: "Review" });
  return base;
}
function stepIndex(steps: StepDef[], key: string) {
  return steps.findIndex((s) => s.key === key) + 1;
}

function isScopeReady(form: FormState, spec: TypeSpec): boolean {
  if (spec.scopeLevel === "branch" || form.scopeLevel === "branch") return !!form.bid;
  if (form.scopeLevel === "ward") return !!form.wid;
  return !!form.cid;
}

// ---- Progress bar -----------------------------------------------------

function Progress({ current, total, labels }: { current: number; total: number; labels: string[] }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 8, fontSize: 12, color: "var(--muted)", marginBottom: 6 }}>
        Step {current} of {total} <span>·</span> <strong style={{ color: "var(--text)" }}>{labels[current - 1]}</strong>
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {labels.map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: i < current ? "var(--accent)" : "var(--border)",
          }} />
        ))}
      </div>
    </div>
  );
}

// ---- Step components -----------------------------------------------------

function Step1Type({ onPick }: { onPick: (t: ItemType) => void }) {
  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>What do you want to create?</h2>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>Pick one. We'll ask the right questions for it.</p>
      <div className="tiles">
        {TYPES.map((t) => (
          <button
            key={t.type}
            onClick={() => onPick(t.type)}
            className="tile"
            style={{ textAlign: "left", border: "1px solid var(--border)", background: "var(--panel)" }}
          >
            <div className="tile-icon">{t.icon}</div>
            <div className="tile-title">{t.label}</div>
            <div className="tile-desc">{t.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ScopeStep({
  spec, form, update, constituencies, wards, branches, stations, ready, onNext, onBack,
}: {
  spec: TypeSpec;
  form: FormState;
  update: (p: Partial<FormState>) => void;
  constituencies: Constituency[];
  wards: Ward[];
  branches: Branch[];
  stations: PollingStation[];
  ready: boolean;
  onNext: () => void;
  onBack: () => void;
}) {
  const allowsLevelChange = !spec.scopeLevel; // POI/popularity can pick the level
  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>Where does this apply?</h2>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>
        {spec.scopeLevel === "branch"
          ? "Pick the branch this is happening in."
          : "Pick how broad the scope is, then the area."}
      </p>

      {allowsLevelChange && (
        <div style={{ marginBottom: 12 }}>
          <label>Scope level</label>
          <select
            value={form.scopeLevel}
            onChange={(e) => update({ scopeLevel: e.target.value as FormState["scopeLevel"], wid: "", bid: "" })}
          >
            <option value="branch">Branch (most specific)</option>
            <option value="ward">Ward</option>
            <option value="constituency">Constituency (broadest)</option>
          </select>
        </div>
      )}

      <label>Constituency</label>
      <select
        value={form.cid}
        onChange={(e) => update({ cid: e.target.value, wid: "", bid: "" })}
      >
        <option value="">— choose —</option>
        {constituencies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>

      {(form.scopeLevel === "ward" || form.scopeLevel === "branch") && (
        <>
          <label style={{ marginTop: 12 }}>Ward</label>
          <select
            value={form.wid}
            onChange={(e) => update({ wid: e.target.value, bid: "" })}
            disabled={!form.cid}
          >
            <option value="">{form.cid ? "— choose —" : "Pick a constituency first"}</option>
            {wards.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </>
      )}

      {form.scopeLevel === "branch" && (
        <>
          <label style={{ marginTop: 12 }}>Branch</label>
          <select
            value={form.bid}
            onChange={(e) => update({ bid: e.target.value, psid: "" })}
            disabled={!form.wid}
          >
            <option value="">{form.wid ? "— choose —" : "Pick a ward first"}</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>

          {/* Optional polling-station narrowing. Only meaningful when a
              branch has stations attached — if there are none, we hide the
              picker entirely so the wizard stays clean. */}
          {form.bid && stations.length > 0 && (
            <>
              <label style={{ marginTop: 12 }}>Polling station <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}>(optional)</span></label>
              <select
                value={form.psid}
                onChange={(e) => update({ psid: e.target.value })}
              >
                <option value="">— branch level (no specific station) —</option>
                {stations.map((ps) => <option key={ps.id} value={ps.id}>{ps.name}</option>)}
              </select>
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                Pick a station if this record happened at one specific polling place. Leaves the record at branch level otherwise.
              </div>
            </>
          )}
        </>
      )}

      <NavRow onBack={onBack} onNext={onNext} canNext={ready} hint="Pick a location to continue" />
    </div>
  );
}

function PerfSubjectStep({
  form, update, users, onNext, onBack,
}: { form: FormState; update: (p: Partial<FormState>) => void; users: AppUser[]; onNext: () => void; onBack: () => void }) {
  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>Who is this report about?</h2>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>Pick the team member this report concerns.</p>
      <label>Subject</label>
      <select value={form.subjectUid} onChange={(e) => update({ subjectUid: e.target.value })}>
        <option value="">— choose a person —</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>)}
      </select>
      <NavRow onBack={onBack} onNext={onNext} canNext={!!form.subjectUid} hint="Pick a person to continue" />
    </div>
  );
}

function PopSubjectStep({
  form, update, users, onNext, onBack,
}: { form: FormState; update: (p: Partial<FormState>) => void; users: AppUser[]; onNext: () => void; onBack: () => void }) {
  const ready = form.subjectKind === "internal" ? !!form.subjectUid : !!form.subjectName;
  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>Who is this report about?</h2>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>
        Pick a registered user, or type the name of someone outside the system.
      </p>
      <label>Subject kind</label>
      <select value={form.subjectKind} onChange={(e) => update({ subjectKind: e.target.value as "internal" | "external" })}>
        <option value="internal">A registered user</option>
        <option value="external">Someone else (free-text)</option>
      </select>

      {form.subjectKind === "internal" ? (
        <>
          <label style={{ marginTop: 12 }}>User</label>
          <select value={form.subjectUid} onChange={(e) => update({ subjectUid: e.target.value })}>
            <option value="">— choose —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>)}
          </select>
        </>
      ) : (
        <>
          <label style={{ marginTop: 12 }}>Full name</label>
          <input value={form.subjectName} onChange={(e) => update({ subjectName: e.target.value })} placeholder="e.g. Joseph Banda" />
          <label style={{ marginTop: 12 }}>Who they are (optional)</label>
          <input value={form.subjectContext} onChange={(e) => update({ subjectContext: e.target.value })} placeholder="e.g. local trader, councillor candidate" />
        </>
      )}
      <NavRow onBack={onBack} onNext={onNext} canNext={ready} hint="Pick or name a subject to continue" />
    </div>
  );
}

function TaskAssigneeStep({
  form, update, users, onNext, onBack,
}: { form: FormState; update: (p: Partial<FormState>) => void; users: AppUser[]; onNext: () => void; onBack: () => void }) {
  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>Who should do this task?</h2>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>Pick the officer to assign it to. They'll get a notification.</p>
      <label>Assignee</label>
      <select value={form.assigneeUid} onChange={(e) => update({ assigneeUid: e.target.value })}>
        <option value="">— choose a person —</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>)}
      </select>
      <NavRow onBack={onBack} onNext={onNext} canNext={!!form.assigneeUid} hint="Pick an assignee to continue" />
    </div>
  );
}

function DetailsStep({
  type, form, update, onNext, onBack,
}: { type: ItemType; form: FormState; update: (p: Partial<FormState>) => void; onNext: () => void; onBack: () => void }) {
  const fields = (() => {
    switch (type) {
      case "case":
        return (
          <>
            <Field label="Title" hint="A short headline. Example: 'Suspicious gathering near Mansa Park'.">
              <input value={form.title} onChange={(e) => update({ title: e.target.value })} />
            </Field>
            <Field label="Summary" hint="What's happening, who's involved, what you've observed so far.">
              <textarea rows={4} value={form.body} onChange={(e) => update({ body: e.target.value })} />
            </Field>
            <Field label="Subject name (optional)" hint="If a specific person is at the centre of this, name them. Otherwise leave blank.">
              <input value={form.caseSubjectName} onChange={(e) => update({ caseSubjectName: e.target.value })} />
            </Field>
            <div className="row-3">
              <Field label="Subject ID number (optional)">
                <input value={form.caseSubjectIdNumber} onChange={(e) => update({ caseSubjectIdNumber: e.target.value })} />
              </Field>
              <Field label="Subject phone (optional)">
                <input value={form.caseSubjectPhone} onChange={(e) => update({ caseSubjectPhone: e.target.value })} />
              </Field>
              <span />
            </div>
          </>
        );
      case "intel":
        return (
          <>
            <Field label="Title" hint="One line. What's the alert about?">
              <input value={form.title} onChange={(e) => update({ title: e.target.value })} />
            </Field>
            <Field label="Body" hint="Details that the people receiving this need to act on.">
              <textarea rows={4} value={form.body} onChange={(e) => update({ body: e.target.value })} />
            </Field>
          </>
        );
      case "performance":
        return (
          <>
            <Field label="Reasons" hint="Why this rating? Be specific — incidents, missed targets, behaviour.">
              <textarea rows={5} value={form.reasons} onChange={(e) => update({ reasons: e.target.value })} />
            </Field>
            <Field label="Recommendations (optional)" hint="What should happen next? Training, reassignment, recognition?">
              <textarea rows={3} value={form.recommendations} onChange={(e) => update({ recommendations: e.target.value })} />
            </Field>
          </>
        );
      case "popularity":
        return (
          <>
            <Field label="Observations" hint="What are people saying about this person? Where? What's the basis for your score?">
              <textarea rows={5} value={form.observations} onChange={(e) => update({ observations: e.target.value })} />
            </Field>
          </>
        );
      case "poi":
        return (
          <>
            <Field label="Full name" hint="Their commonly-used full name.">
              <input value={form.subjectName} onChange={(e) => update({ subjectName: e.target.value })} />
            </Field>
            <Field label="Aliases (comma-separated, optional)">
              <input value={form.aliases} onChange={(e) => update({ aliases: e.target.value })} placeholder="e.g. JB, Junior" />
            </Field>
            <div className="row-3">
              <Field label="ID number (optional)">
                <input value={form.idNumber} onChange={(e) => update({ idNumber: e.target.value })} />
              </Field>
              <Field label="Phone (optional)">
                <input value={form.phone} onChange={(e) => update({ phone: e.target.value })} />
              </Field>
              <Field label="Email (optional)">
                <input value={form.email} onChange={(e) => update({ email: e.target.value })} />
              </Field>
            </div>
            <Field label="Address (optional)">
              <input value={form.address} onChange={(e) => update({ address: e.target.value })} />
            </Field>
            <Field label="Date of birth (YYYY-MM-DD, optional)">
              <input value={form.dateOfBirth} onChange={(e) => update({ dateOfBirth: e.target.value })} placeholder="1985-03-21" />
            </Field>
            <Field label="Notes" hint="Anything else worth recording.">
              <textarea rows={4} value={form.notes} onChange={(e) => update({ notes: e.target.value })} />
            </Field>
          </>
        );
      case "task":
        return (
          <>
            <Field label="Title" hint="One line. What needs to be done?">
              <input value={form.title} onChange={(e) => update({ title: e.target.value })} />
            </Field>
            <Field label="Description" hint="Anything the assignee needs to know to do it well.">
              <textarea rows={4} value={form.description} onChange={(e) => update({ description: e.target.value })} />
            </Field>
            <Field label="Due (optional)">
              <input type="datetime-local" value={form.dueAt} onChange={(e) => update({ dueAt: e.target.value })} />
            </Field>
          </>
        );
    }
  })();

  const canNext = (() => {
    switch (type) {
      case "case": return !!form.title && !!form.body;
      case "intel": return !!form.title && !!form.body;
      case "performance": return !!form.reasons;
      case "popularity": return !!form.observations;
      case "poi": return !!form.subjectName;
      case "task": return !!form.title;
    }
  })();

  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>The details</h2>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>Fill in what you know. Required fields are marked.</p>
      {fields}
      <NavRow onBack={onBack} onNext={onNext} canNext={canNext} hint="Fill in the required fields to continue" />
    </div>
  );
}

function SensitivityStep({
  type, form, update, onNext, onBack,
}: { type: ItemType; form: FormState; update: (p: Partial<FormState>) => void; onNext: () => void; onBack: () => void }) {
  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>How sensitive is this?</h2>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>This controls who can see it.</p>

      {/* Classification — applies to most types */}
      {type !== "task" && (
        <Field label="Classification" hint="Restricted and Secret are only visible to people with elevated access.">
          <select value={form.classification} onChange={(e) => update({ classification: e.target.value as FormState["classification"] })}>
            <option value="unclassified">Unclassified — anyone in scope can read</option>
            <option value="restricted">Restricted — limited reads</option>
            <option value="secret">Secret — only cleared readers</option>
          </select>
        </Field>
      )}

      {/* Per-type extras */}
      {type === "case" && (
        <>
          <Field label="Kind" hint="Investigation is a formal case file. Observation/alert/note are lighter intel-style entries.">
            <select value={form.kind} onChange={(e) => update({ kind: e.target.value as FormState["kind"] })}>
              <option value="investigation">Investigation</option>
              <option value="observation">Observation</option>
              <option value="alert">Alert</option>
              <option value="note">Note</option>
            </select>
          </Field>
          {form.kind !== "investigation" && (
            <Field label="Severity" hint="How urgent? Critical alerts can wake people at night.">
              <select value={form.severity} onChange={(e) => update({ severity: e.target.value as FormState["severity"] })}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </Field>
          )}
        </>
      )}
      {type === "intel" && (
        <>
          <Field label="Severity" hint="How urgent? Critical alerts can wake people at night.">
            <select value={form.severity} onChange={(e) => update({ severity: e.target.value as FormState["severity"] })}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </Field>
          <Field label="Kind">
            <select
              value={form.kind === "investigation" ? "observation" : form.kind}
              onChange={(e) => update({ kind: e.target.value as FormState["kind"] })}
            >
              <option value="note">Note</option>
              <option value="observation">Observation</option>
              <option value="alert">Alert</option>
            </select>
          </Field>
        </>
      )}
      {type === "performance" && (
        <Field label="Rating" hint="What's the overall judgement?">
          <select value={form.rating} onChange={(e) => update({ rating: e.target.value as FormState["rating"] })}>
            <option value="failing">Failing</option>
            <option value="poor">Poor</option>
            <option value="satisfactory">Satisfactory</option>
            <option value="good">Good</option>
            <option value="excellent">Excellent</option>
          </select>
        </Field>
      )}
      {type === "popularity" && (
        <Field label="How popular?" hint="Use what you've heard, not what you wish.">
          <select value={form.popularityScore} onChange={(e) => update({ popularityScore: e.target.value as FormState["popularityScore"] })}>
            <option value="very_low">Very low</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="very_high">Very high</option>
          </select>
        </Field>
      )}
      {type === "poi" && (
        <>
          <Field label="Threat level" hint="Your assessment of how serious watching this person is.">
            <select value={form.threatLevel} onChange={(e) => update({ threatLevel: e.target.value as FormState["threatLevel"] })}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </Field>
          <Field label="Tags" hint="Tick everything that fits. A person can be 'popular' AND 'donor' AND 'community_leader' at the same time.">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
              {POI_TAG_ORDER.map((tag) => {
                const active = form.poiTags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => update({
                      poiTags: active
                        ? form.poiTags.filter((t) => t !== tag)
                        : [...form.poiTags, tag],
                    })}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 999,
                      fontSize: 12,
                      cursor: "pointer",
                      border: active ? "1px solid var(--accent)" : "1px solid var(--border)",
                      background: active ? "var(--accent)" : "transparent",
                      color: active ? "#fff" : "var(--text)",
                    }}
                  >
                    {POI_TAG_LABELS[tag]}
                  </button>
                );
              })}
            </div>
          </Field>
        </>
      )}
      {type === "task" && (
        <Field label="Priority" hint="How urgent is the task?">
          <select value={form.priority} onChange={(e) => update({ priority: e.target.value as FormState["priority"] })}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </Field>
      )}

      {/* Sources + confidence — universal intel metadata. Tasks don't carry
          an assessment, so we hide it there. Both inputs are optional. */}
      {type !== "task" && (
        <div style={{ marginTop: 16, padding: 12, background: "var(--bg-muted, #f7f7f9)", borderRadius: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Intel quality</div>
          <p className="muted" style={{ fontSize: 12, margin: "0 0 8px" }}>
            How many independent sources told you this, and how confident are you in the assessment? Both optional.
          </p>
          <div className="row-3" style={{ gap: 8 }}>
            <Field label="Sources">
              <input
                type="number"
                min={0}
                value={form.sourcesCount}
                onChange={(e) => update({ sourcesCount: e.target.value })}
                placeholder="e.g. 3"
              />
            </Field>
            <Field label="Confidence (0–100)">
              <input
                type="number"
                min={0}
                max={100}
                value={form.confidenceScore}
                onChange={(e) => update({ confidenceScore: e.target.value })}
                placeholder="e.g. 70"
              />
            </Field>
            <span />
          </div>
        </div>
      )}

      <NavRow onBack={onBack} onNext={onNext} canNext={true} />
    </div>
  );
}

function ReviewStep({
  type, spec, form, busy, onBack, onSubmit,
}: { type: ItemType; spec: TypeSpec; form: FormState; busy: boolean; onBack: () => void; onSubmit: () => void }) {
  const summary = summarizeForm(type, form);
  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>Review and submit</h2>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>One last look. If it's right, click Submit.</p>

      <dl className="kv">
        <dt>What</dt><dd>{spec.label}</dd>
        {summary.map(([k, v]) => <FragRow key={k} k={k} v={v} />)}
      </dl>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
        <button className="ghost" onClick={onBack} disabled={busy}>Back</button>
        <button onClick={onSubmit} disabled={busy}>{busy ? "Submitting…" : "Submit"}</button>
      </div>
    </div>
  );
}

function FragRow({ k, v }: { k: string; v: string }) { return (<><dt>{k}</dt><dd>{v}</dd></>); }

function summarizeForm(type: ItemType, f: FormState): [string, string][] {
  const where =
    f.scopeLevel === "branch" ? `Branch ${f.bid}` :
    f.scopeLevel === "ward"   ? `Ward ${f.wid}` :
    `Constituency ${f.cid}`;
  const out: [string, string][] = [["Where", where]];
  switch (type) {
    case "case":
      out.push(["Title", f.title], ["Summary", f.body], ["Classification", f.classification]);
      if (f.caseSubjectName) out.push(["Subject", f.caseSubjectName]);
      break;
    case "intel":
      out.push(["Title", f.title], ["Body", f.body], ["Kind", f.kind], ["Severity", f.severity], ["Classification", f.classification]);
      break;
    case "performance":
      out.push(["Subject", f.subjectUid], ["Rating", f.rating], ["Reasons", f.reasons]);
      if (f.recommendations) out.push(["Recommendations", f.recommendations]);
      out.push(["Classification", f.classification]);
      break;
    case "popularity":
      out.push(["Subject kind", f.subjectKind]);
      out.push(["Subject", f.subjectKind === "internal" ? f.subjectUid : f.subjectName]);
      out.push(["Score", f.popularityScore.replace("_", " ")], ["Observations", f.observations], ["Classification", f.classification]);
      break;
    case "poi":
      out.push(["Name", f.subjectName], ["Threat", f.threatLevel], ["Classification", f.classification]);
      if (f.notes) out.push(["Notes", f.notes]);
      break;
    case "task":
      out.push(["Title", f.title], ["Assignee", f.assigneeUid], ["Priority", f.priority]);
      if (f.dueAt) out.push(["Due", f.dueAt]);
      break;
  }
  return out;
}

// ---- Reusable bits -----------------------------------------------------

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div style={{ marginTop: 10 }}>
      <label>{label}</label>
      {children}
      {hint && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function NavRow({ onBack, onNext, canNext, hint }: { onBack: () => void; onNext: () => void; canNext: boolean; hint?: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18 }}>
      <button className="ghost" onClick={onBack}>Back</button>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        {!canNext && hint && <span className="muted" style={{ fontSize: 12 }}>{hint}</span>}
        <button onClick={onNext} disabled={!canNext}>Next</button>
      </div>
    </div>
  );
}
